/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RiskMeterWidget - Burbuja Fija + Ventana Chat (Estilo Facebook)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file RiskMeterWidget.tsx
 * @version 3.2.0 - FINAL: Burbuja fija con botón minimizar
 * @description Burbuja fija que abre ventana de chat flotante
 * 
 * COMPORTAMIENTO:
 * - Burbuja FIJA (NO draggable)
 * - Botón minimizar [-] oculta la burbuja
 * - Burbuja reaparece al volver al tab Risk Meter (prop visible)
 * - Click en burbuja → Abre ventana chat con z-index 99999
 * - Ventana draggable, siempre sobre todo
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, Badge, Button, Nav, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faMinus,
  faTachometerAlt,
  faListCheck,
  faLightbulb,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

// Componentes de Fase 2
import RiskGauge from './RiskGauge';
import RiskFactorCard from './RiskFactorCard';
import RecommendationPanel from './RecommendationPanel';

// WindowManager para z-index dinámico
import { useWindowManager } from '../../hooks/useWindowManager';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type FactorStatus = 'excellent' | 'good' | 'warning' | 'danger';

interface Position {
  x: number;
  y: number;
}

interface WhatsAppRiskFactor {
  id: string;
  name: string;
  description: string;
  weight: number;
  currentValue: any;
  optimalRange: any;
  score: number;
  status: FactorStatus;
  recommendation?: string;
}

interface WhatsAppRiskAssessment {
  overallScore: number;
  riskLevel: RiskLevel;
  flagProbability: number;
  factors: WhatsAppRiskFactor[];
  recommendations: string[];
  timestamp?: number;
}

interface RiskMeterWidgetProps {
  riskData: WhatsAppRiskAssessment | null;
  visible?: boolean; // Controlado por tab activo de BulkSend
  onMinimize?: () => void; // Callback cuando se minimiza
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const BUBBLE_POSITION: Position = { x: 20, y: 100 }; // Posición fija
const WINDOW_SIZE = { width: 420, height: 600 };
const SUPER_HIGH_Z_INDEX = 99999;

const LEVEL_COLORS: Record<RiskLevel, string> = {
  low: '#22c55e',
  medium: '#eab308',
  high: '#f97316',
  critical: '#dc2626'
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const calculateWindowPos = (bubblePos: Position): Position => ({
  x: Math.max(20, bubblePos.x + 110), // A la derecha de la burbuja
  y: Math.max(20, bubblePos.y - 50)
});

const calculateNeedleAngle = (score: number): number => {
  return (Math.min(100, Math.max(0, score)) / 100) * 180 - 90;
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const RiskMeterWidget: React.FC<RiskMeterWidgetProps> = ({
  riskData,
  visible = true,
  onMinimize
}) => {
  // ───────────────────────────────────────────────────────────────────────
  // WINDOW MANAGER
  // ───────────────────────────────────────────────────────────────────────
  
  const windowManager = useWindowManager();
  const [windowId] = useState(() => `risk-chat-${Date.now()}`);
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [bubbleVisible, setBubbleVisible] = useState(true); // Estado interno de minimizado
  const [showWindow, setShowWindow] = useState(false);
  const [windowPos, setWindowPos] = useState<Position>(() => calculateWindowPos(BUBBLE_POSITION));
  const [isDraggingWindow, setIsDraggingWindow] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [isAnimating, setIsAnimating] = useState(false);
  
  const windowRef = useRef<HTMLDivElement>(null);
  
  // ───────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────────
  
  // Cuando visible cambia (cambio de tab), restaurar burbuja
  useEffect(() => {
    if (visible) {
      setBubbleVisible(true);
    }
  }, [visible]);
  
  // Registrar ventana en WindowManager al abrirla
  useEffect(() => {
    if (showWindow && riskData) {
      windowManager.registerWindow({
        id: windowId,
        type: 'widget',
        position: windowPos,
        size: WINDOW_SIZE,
        metadata: { component: 'RiskMeterChat', zIndexOverride: SUPER_HIGH_Z_INDEX }
      });
      
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 400);
      
      return () => {
        windowManager.closeWindow(windowId);
      };
    }
  }, [showWindow, riskData]);
  
  // ───────────────────────────────────────────────────────────────────────
  // DRAG HANDLERS - VENTANA
  // ───────────────────────────────────────────────────────────────────────
  
  const handleWindowMouseDown = (e: React.MouseEvent) => {
    windowManager.bringToFront(windowId);
    
    const target = e.target as HTMLElement;
    if (!target.closest('.window-drag-handle')) return;
    
    e.preventDefault();
    setIsDraggingWindow(true);
    
    if (windowRef.current) {
      const rect = windowRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };
  
  useEffect(() => {
    if (!isDraggingWindow) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const newPos = {
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      };
      setWindowPos(newPos);
      windowManager.updateWindowPosition(windowId, newPos);
    };
    
    const handleMouseUp = () => setIsDraggingWindow(false);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingWindow, dragOffset, windowId, windowManager]);
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleBubbleClick = () => {
    if (riskData) {
      setWindowPos(calculateWindowPos(BUBBLE_POSITION));
      setShowWindow(true);
    }
  };
  
  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBubbleVisible(false);
    if (onMinimize) onMinimize();
  };
  
  const handleCloseWindow = () => {
    setShowWindow(false);
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: VELOCÍMETRO MINI
  // ───────────────────────────────────────────────────────────────────────
  
  const renderSpeedometer = () => {
    if (!riskData) return null;
    
    const size = 70;
    const angle = calculateNeedleAngle(riskData.overallScore);
    const color = LEVEL_COLORS[riskData.riskLevel];
    const cx = size / 2;
    const cy = size / 2;
    const r = (size / 2) * 0.75;
    const needleLen = r * 0.8;
    const nx = cx + needleLen * Math.cos((angle - 90) * Math.PI / 180);
    const ny = cy + needleLen * Math.sin((angle - 90) * Math.PI / 180);
    
    return (
      <svg width={size} height={size}>
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="var(--bs-border-color)"
          strokeWidth={size * 0.1}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r * Math.cos((angle + 90) * Math.PI / 180)} ${cy + r * Math.sin((angle + 90) * Math.PI / 180)}`}
          fill="none"
          stroke={color}
          strokeWidth={size * 0.1}
          strokeLinecap="round"
          style={{ transition: 'all 0.5s ease' }}
        />
        <circle cx={cx} cy={cy} r={size * 0.06} fill={color} />
        <line
          x1={cx} y1={cy} x2={nx} y2={ny}
          stroke={color}
          strokeWidth={size * 0.04}
          strokeLinecap="round"
          style={{ transition: 'all 0.5s ease' }}
        />
        <text
          x={cx} y={cy + size * 0.15}
          textAnchor="middle"
          style={{ fontSize: `${size * 0.3}px`, fontWeight: 700, fill: 'var(--bs-body-color)' }}
        >
          {Math.round(riskData.overallScore)}
        </text>
        <text
          x={cx} y={cy + size * 0.42}
          textAnchor="middle"
          style={{ fontSize: `${size * 0.14}px`, fontWeight: 600, fill: color, textTransform: 'uppercase' }}
        >
          {riskData.riskLevel}
        </text>
      </svg>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: VENTANA FLOTANTE
  // ───────────────────────────────────────────────────────────────────────
  
  const renderChatWindow = () => {
    if (!showWindow || !riskData) return null;
    
    const windowContent = (
      <div
        ref={windowRef}
        style={{
          position: 'fixed',
          left: `${windowPos.x}px`,
          top: `${windowPos.y}px`,
          width: `${WINDOW_SIZE.width}px`,
          height: `${WINDOW_SIZE.height}px`,
          zIndex: SUPER_HIGH_Z_INDEX,
          transform: isAnimating ? 'scale(0.7) translateY(20px)' : 'scale(1) translateY(0)',
          opacity: isAnimating ? 0 : 1,
          transition: isAnimating 
            ? 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
            : 'opacity 0.2s ease',
          cursor: isDraggingWindow ? 'grabbing' : 'default'
        }}
        onMouseDown={handleWindowMouseDown}
      >
        <Card
          style={{
            height: '100%',
            border: '2px solid #007bff',
            borderRadius: '12px',
            boxShadow: '0 16px 48px rgba(0,123,255,0.4)',
            backgroundColor: 'var(--bs-body-bg)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Card.Header
            className="window-drag-handle"
            style={{
              cursor: 'grab',
              backgroundColor: '#007bff',
              color: 'white',
              borderBottom: 'none',
              padding: '12px 16px',
              userSelect: 'none',
              borderRadius: '10px 10px 0 0'
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Risk Meter
                </h6>
                <Badge bg="light" text="dark" style={{ fontSize: '0.65rem' }}>
                  {Math.round(riskData.overallScore)}
                </Badge>
              </div>
              <Button
                variant="link"
                size="sm"
                onClick={handleCloseWindow}
                style={{ padding: '4px 8px', color: 'white', textDecoration: 'none' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </Card.Header>
          
          <Card.Body style={{ flex: 1, overflow: 'hidden', padding: '12px' }}>
            <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
              <Nav variant="pills" className="mb-3" style={{ fontSize: '0.85rem' }}>
                <Nav.Item>
                  <Nav.Link eventKey="overview" style={{ padding: '6px 12px' }}>
                    <FontAwesomeIcon icon={faTachometerAlt} className="me-1" />
                    Overview
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="factors" style={{ padding: '6px 12px' }}>
                    <FontAwesomeIcon icon={faListCheck} className="me-1" />
                    Factors
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="tips" style={{ padding: '6px 12px' }}>
                    <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                    Tips
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              
              <Tab.Content style={{ height: 'calc(100% - 45px)', overflowY: 'auto' }}>
                <Tab.Pane eventKey="overview">
                  <div className="text-center mb-3">
                    <RiskGauge score={riskData.overallScore} level={riskData.riskLevel} size={140} />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <Card className="text-center">
                        <Card.Body className="p-2">
                          <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                            Flag Probability
                          </small>
                          <h5 className="mb-0" style={{ color: LEVEL_COLORS[riskData.riskLevel] }}>
                            {Math.round(riskData.flagProbability)}%
                          </h5>
                        </Card.Body>
                      </Card>
                    </div>
                    <div className="col-6">
                      <Card className="text-center">
                        <Card.Body className="p-2">
                          <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                            Risk Level
                          </small>
                          <h5 className="mb-0" style={{ color: LEVEL_COLORS[riskData.riskLevel] }}>
                            {riskData.riskLevel.toUpperCase()}
                          </h5>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                  <div className="d-grid gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setActiveTab('factors')}>
                      Ver Factores Detallados
                    </Button>
                  </div>
                </Tab.Pane>
                
                <Tab.Pane eventKey="factors">
                  <div className="d-flex flex-column gap-2" style={{ fontSize: '0.85rem' }}>
                    {riskData.factors.map(f => <RiskFactorCard key={f.id} factor={f} showWeight />)}
                  </div>
                </Tab.Pane>
                
                <Tab.Pane eventKey="tips">
                  <RecommendationPanel recommendations={riskData.recommendations} />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card.Body>
        </Card>
      </div>
    );
    
    return createPortal(windowContent, document.body);
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ───────────────────────────────────────────────────────────────────────
  
  if (!riskData || !visible || !bubbleVisible) return null;
  
  return (
    <>
      {/* BURBUJA FIJA */}
      <div
        style={{
          position: 'absolute',
          right: `${BUBBLE_POSITION.x}px`,
          bottom: `${BUBBLE_POSITION.y}px`,
          width: '90px',
          height: '90px',
          cursor: 'pointer',
          zIndex: 999
        }}
        onClick={handleBubbleClick}
      >
        <Card
          style={{
            width: '100%',
            height: '100%',
            border: '2px solid #007bff',
            borderRadius: '50%',
            boxShadow: '0 4px 16px rgba(0,123,255,0.3)',
            backgroundColor: 'var(--bs-body-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* Botón minimizar */}
          <button
            onClick={handleMinimize}
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              zIndex: 1
            }}
          >
            <FontAwesomeIcon 
              icon={faMinus} 
              style={{ fontSize: '10px', color: '#007bff', opacity: 0.7 }} 
            />
          </button>
          
          {/* Velocímetro */}
          <div style={{ marginTop: '8px' }}>
            {renderSpeedometer()}
          </div>
        </Card>
      </div>
      
      {/* VENTANA FLOTANTE */}
      {renderChatWindow()}
      
      <style>{`
        .window-drag-handle:active { cursor: grabbing !important; }
      `}</style>
    </>
  );
};

export default RiskMeterWidget;